import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gameLogger } from '../../../utils/GameLogger';

/**
 * DiagnosticCheckpointBody — the zero-chrome body of a checkpoint diagnostic
 * panel: polls a list of `check()` functions, renders pass/fail/detail rows
 * grouped by category, with a "copy to clipboard" button + optional infoString.
 *
 * Extracted from the old BaseDiagnosticPanel (which wrapped this in a
 * ToolWindow). The 🛠 kit provides the window now, so the migrated diagnostic
 * tools render this body directly inside the kit's ToolWindow — no double
 * chrome. Used by PhysicsDiagnosticPanelContent + RenderingDiagnosticPanelContent.
 */

export interface DiagnosticCheckpoint {
  label: string;
  category: string;
  check: () => boolean | string; // true = pass, false = fail, string = detail
}

export interface DiagnosticCheckpointBodyProps {
  /** Ordered list of categories and their display labels. */
  categories: { id: string; label: string }[];
  /** The specific checkpoints to evaluate. */
  checkpoints: DiagnosticCheckpoint[];
  /** Optional top-level text information (e.g. FPS / Time). */
  infoString?: string;
  /** How often to poll the check functions (ms). */
  pollIntervalMs?: number;
  /** Background style for the body. */
  backgroundOverlay?: string;
}

const statusIcon = (val: boolean | string): string => {
  if (val === true) return '✓';
  if (val === false) return '✗';
  return '?';
};

const statusColor = (val: boolean | string): string => {
  if (val === true) return '#00ff88';
  if (val === false) return '#ff4444';
  return '#ffaa00';
};

export const DiagnosticCheckpointBody: React.FC<DiagnosticCheckpointBodyProps> = ({
  categories,
  checkpoints,
  infoString = '',
  pollIntervalMs = 500,
  backgroundOverlay = 'rgba(0,0,0,0.7)',
}) => {
  const [results, setResults] = useState<{ label: string; category: string; value: boolean | string }[]>([]);
  const [copied, setCopied] = useState(false);

  // Keep a stable ref to the latest checkpoints so runChecks never recreates.
  const checkpointsRef = useRef(checkpoints);
  checkpointsRef.current = checkpoints;

  const runChecks = useCallback(() => {
    const newResults = checkpointsRef.current.map(cp => {
      try {
        return { label: cp.label, category: cp.category, value: cp.check() };
      } catch {
        return { label: cp.label, category: cp.category, value: 'Error computing check' };
      }
    });
    setResults(newResults);
  }, []);

  const formatAsText = useCallback((items: typeof results, info: string): string => {
    const lines: string[] = [info].filter(Boolean);
    const categoryMap = new Map(categories.map(c => [c.id, c.label]));
    for (const cat of categories) {
      const catItems = items.filter(r => r.category === cat.id);
      if (catItems.length === 0) continue;
      lines.push(categoryMap.get(cat.id) || cat.id);
      for (const r of catItems) {
        const icon = r.value === true ? '✓' : r.value === false ? '✗' : '?';
        const val = r.value === true ? 'OK' : r.value === false ? 'MISSING' : r.value;
        lines.push(`${icon} ${r.label}: ${val}`);
      }
    }
    return lines.join('\n');
  }, [categories]);

  useEffect(() => {
    runChecks();
    if (pollIntervalMs > 0) {
      const interval = setInterval(runChecks, pollIntervalMs);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [runChecks, pollIntervalMs]);

  const handleManualCopy = () => {
    const text = formatAsText(results, infoString);
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(err => gameLogger.error('ui', 'Clipboard copy failed:', err));
  };

  const passCount = results.filter(r => r.value === true || (typeof r.value === 'string' && r.value !== '')).length;
  const totalCount = results.length;

  return (
    <div style={{
      padding: '10px',
      background: backgroundOverlay,
      textAlign: 'left',
      fontSize: '11px',
      fontFamily: 'monospace',
      height: '100%',
      overflow: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ color: '#888', fontSize: '10px' }}>{passCount}/{totalCount} OK</span>
        <button
          onClick={handleManualCopy}
          style={{
            background: copied ? '#00ff8833' : 'rgba(255,255,255,0.1)',
            border: copied ? '1px solid #00ff88' : '1px solid #555',
            color: copied ? '#00ff88' : '#aaa',
            cursor: 'pointer',
            fontSize: '10px',
            padding: '2px 8px',
            borderRadius: '3px',
            fontFamily: 'monospace',
            transition: 'all 0.2s',
          }}
          title="Copy diagnostics to clipboard"
        >
          {copied ? '✓ Copied!' : '📋 Copy Text'}
        </button>
      </div>

      {infoString && (
        <div style={{ color: '#888', marginBottom: '12px', fontSize: '10px', borderBottom: '1px solid #333', paddingBottom: '8px' }}>
          {infoString}
        </div>
      )}

      {categories.map(cat => {
        const catResults = results.filter(r => r.category === cat.id);
        if (catResults.length === 0) return null;
        return (
          <div key={cat.id} style={{ marginBottom: '10px' }}>
            <div style={{ color: '#aaa', fontSize: '11px', marginBottom: '4px', fontWeight: 'bold' }}>
              {cat.label}
            </div>
            {catResults.map((r, i) => (
              <div key={i} style={{ paddingLeft: '8px', color: statusColor(r.value), marginBottom: '2px' }}>
                <span style={{ display: 'inline-block', width: '12px' }}>{statusIcon(r.value)}</span>
                <span>{r.label}: </span>
                <span style={{ color: typeof r.value === 'string' ? '#ffcc00' : statusColor(r.value) }}>
                  {r.value === true ? 'OK' : r.value === false ? 'MISSING' : r.value}
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};
